import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  Theme,
  createStyles,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';
import RestoreIcon from '@material-ui/icons/Restore';
import { VariableEditorProps, TerraformVariableSchema, TerraformType, isHiddenValue, isDerivedValue } from '../types';
import { VariableEditor } from '../VariableEditor';
import { ItemActions } from './ItemActions';
import { CollapsibleItem } from './CollapsibleItem';
import { ValidationHints } from './ValidationHints';
import { getDefaultForType, deepClone, getObjectSummary } from './utils';
import { useEditorContext } from '../EditorContext';

/**
 * Infer a TerraformVariableSchema from a value
 * This allows the GUI to break down objects/lists even without explicit schema
 */
function inferSchemaFromValue(name: string, value: unknown): TerraformVariableSchema {
  if (value === null || value === undefined) {
    return { name, type: 'any', required: false, sensitive: false, nullable: true };
  }
  if (typeof value === 'string') {
    return { name, type: 'string', required: false, sensitive: false };
  }
  if (typeof value === 'number') {
    return { name, type: 'number', required: false, sensitive: false };
  }
  if (typeof value === 'boolean') {
    return { name, type: 'bool', required: false, sensitive: false };
  }
  if (Array.isArray(value)) {
    const firstElement = value.find(el => el !== null && el !== undefined);
    let elementType: TerraformType = 'any';
    let elementSchema: TerraformVariableSchema | undefined;

    if (firstElement !== undefined) {
      if (typeof firstElement === 'string') elementType = 'string';
      else if (typeof firstElement === 'number') elementType = 'number';
      else if (typeof firstElement === 'boolean') elementType = 'bool';
      else if (typeof firstElement === 'object' && !Array.isArray(firstElement)) {
        elementType = 'object';
        const objectSchema: Record<string, TerraformVariableSchema> = {};
        for (const [key, val] of Object.entries(firstElement as Record<string, unknown>)) {
          objectSchema[key] = inferSchemaFromValue(key, val);
        }
        elementSchema = { name: 'element', type: 'object', required: false, sensitive: false, objectSchema };
      }
    }
    return { name, type: 'list', required: false, sensitive: false, elementType, elementSchema };
  }
  if (typeof value === 'object') {
    const objectSchema: Record<string, TerraformVariableSchema> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      objectSchema[key] = inferSchemaFromValue(key, val);
    }
    return { name, type: 'object', required: false, sensitive: false, objectSchema };
  }
  return { name, type: 'any', required: false, sensitive: false };
}

/**
 * Infer element type from array items when not explicitly specified
 */
function inferElementTypeFromItems(items: unknown[]): { elementType: TerraformType; elementSchema?: TerraformVariableSchema } {
  const firstElement = items.find(el => el !== null && el !== undefined);

  if (firstElement === undefined) {
    return { elementType: 'any' };
  }

  if (typeof firstElement === 'string') return { elementType: 'string' };
  if (typeof firstElement === 'number') return { elementType: 'number' };
  if (typeof firstElement === 'boolean') return { elementType: 'bool' };

  if (typeof firstElement === 'object' && !Array.isArray(firstElement)) {
    const objectSchema: Record<string, TerraformVariableSchema> = {};
    for (const [key, val] of Object.entries(firstElement as Record<string, unknown>)) {
      objectSchema[key] = inferSchemaFromValue(key, val);
    }
    return {
      elementType: 'object',
      elementSchema: { name: 'element', type: 'object', required: false, sensitive: false, objectSchema },
    };
  }

  return { elementType: 'any' };
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
    },
    containerHidden: {
      opacity: 0.5,
      border: `1px dashed ${theme.palette.divider}`,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1),
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(1),
    },
    chipHidden: {
      opacity: 0.5,
    },
    inlineAddContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    inlineInput: {
      flex: 1,
      maxWidth: 300,
    },
    stringItemRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
    expandableInput: {
      '& .MuiInputBase-input': {
        minWidth: '20ch',
        width: 'auto',
      },
    },
    deleteButton: {
      padding: theme.spacing(0.5),
      minWidth: 'auto',
    },
    itemRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
      padding: theme.spacing(1),
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.02)'
          : 'rgba(0, 0, 0, 0.02)',
      borderRadius: theme.shape.borderRadius,
    },
    itemRowHidden: {
      opacity: 0.5,
    },
    itemContent: {
      flex: 1,
    },
    addButton: {
      marginTop: theme.spacing(1),
    },
    itemActions: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: theme.spacing(0.5),
    },
    badge: {
      fontSize: '0.7rem',
      padding: '2px 6px',
      borderRadius: 4,
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.08)',
      color: theme.palette.text.secondary,
    },
    nullBadge: {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
    },
    derivedBadge: {
      backgroundColor: theme.palette.info.light,
      color: theme.palette.info.contrastText,
    },
    boolChip: {
      minWidth: 50,
      justifyContent: 'center',
    },
    boolTrue: {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
    },
    boolFalse: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
    },
    boolNull: {
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.getContrastText(theme.palette.grey[400]),
    },
    showHiddenButton: {
      marginTop: theme.spacing(1),
    },
  }),
);

// Dialog for adding items with type-specific input
interface AddItemDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (value: unknown) => void;
  elementType: string;
  allowNull?: boolean;
}

const AddItemDialog: React.FC<AddItemDialogProps> = ({
  open,
  onClose,
  onAdd,
  elementType,
  allowNull,
}) => {
  const [value, setValue] = useState<string>('');
  const [boolValue, setBoolValue] = useState<boolean | null>(false);

  const handleAdd = () => {
    if (elementType === 'bool') {
      onAdd(boolValue);
    } else if (elementType === 'number') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        onAdd(num);
      }
    } else {
      onAdd(value);
    }
    setValue('');
    setBoolValue(false);
    onClose();
  };

  const handleAddNull = () => {
    onAdd(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Add {elementType === 'bool' ? 'Boolean' : elementType === 'number' ? 'Number' : 'String'} Item
      </DialogTitle>
      <DialogContent>
        {elementType === 'bool' ? (
          <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 8 }}>
            <InputLabel>Value</InputLabel>
            <Select
              value={boolValue === null ? 'null' : boolValue ? 'true' : 'false'}
              onChange={e => {
                const val = e.target.value;
                if (val === 'null') setBoolValue(null);
                else if (val === 'true') setBoolValue(true);
                else setBoolValue(false);
              }}
              label="Value"
            >
              <MenuItem value="true">true</MenuItem>
              <MenuItem value="false">false</MenuItem>
              {allowNull && <MenuItem value="null">null</MenuItem>}
            </Select>
          </FormControl>
        ) : elementType === 'number' ? (
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
            type="number"
            label="Number"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
            style={{ marginTop: 8 }}
          />
        ) : (
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
            label="Text"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
            style={{ marginTop: 8 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        {allowNull && elementType !== 'bool' && (
          <Button onClick={handleAddNull} color="default">
            Add Null
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleAdd} color="primary" variant="contained">
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ListEditor: React.FC<VariableEditorProps> = ({
  variable,
  value,
  onChange,
  error,
  disabled,
  depth = 0,
}) => {
  const classes = useStyles();
  const { showHidden, setShowHidden } = useEditorContext();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [focusNewItemIndex, setFocusNewItemIndex] = useState<number | null>(null);
  const newItemRef = useRef<HTMLInputElement>(null);
  const items = Array.isArray(value) ? value : [];

  // Focus the newly added item
  useEffect(() => {
    if (focusNewItemIndex !== null && newItemRef.current) {
      newItemRef.current.focus();
      setFocusNewItemIndex(null);
    }
  }, [focusNewItemIndex, items.length]);

  // Use explicit element type/schema if available, otherwise infer from items
  const explicitElementType = variable.elementType;
  const explicitElementSchema = variable.elementSchema;

  const inferredInfo = !explicitElementType && items.length > 0
    ? inferElementTypeFromItems(items)
    : undefined;

  const elementType = explicitElementType || inferredInfo?.elementType || 'string';
  const effectiveElementSchema = explicitElementSchema || inferredInfo?.elementSchema;

  const isSimpleType = ['string', 'number', 'bool'].includes(elementType);
  const isObjectList = elementType === 'object' && (effectiveElementSchema || items.some(item => item && typeof item === 'object'));
  const isBoolList = elementType === 'bool';

  // Check if list value is hidden
  const isListHidden = isHiddenValue(value, variable);
  const isListDerived = isDerivedValue(value);

  // Count hidden items
  const hiddenItemCount = items.filter(item => {
    if (item === null) return true;
    if (isDerivedValue(item)) return true;
    return false;
  }).length;

  const handleAddItem = () => {
    const newItem = getDefaultForType(elementType, effectiveElementSchema);
    onChange([...items, newItem]);
  };

  // Add an item inline and focus it (for simple types)
  const handleAddItemInline = (defaultValue: unknown = '') => {
    const newIndex = items.length;
    onChange([...items, defaultValue]);
    setFocusNewItemIndex(newIndex);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
  };

  const handleDuplicateItem = (index: number) => {
    const newItems = [...items];
    const clonedItem = deepClone(items[index]);
    newItems.splice(index + 1, 0, clonedItem);
    onChange(newItems);
  };

  const handleItemChange = (index: number, newValue: unknown) => {
    const newItems = [...items];
    newItems[index] = newValue;
    onChange(newItems);
  };

  const handleSetItemNull = (index: number) => {
    handleItemChange(index, null);
  };

  const handleRestoreItem = (index: number) => {
    const defaultValue = getDefaultForType(elementType, effectiveElementSchema);
    handleItemChange(index, defaultValue);
  };

  // Handle setting the entire list to null
  const handleSetNull = () => {
    onChange(null);
  };

  const handleRestoreFromNull = () => {
    onChange([]);
  };

  // Filter visible items
  const getVisibleItems = () => {
    if (showHidden) return items.map((item, index) => ({ item, index }));
    return items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => {
        if (item === null) return false;
        if (isDerivedValue(item)) return false;
        return true;
      });
  };

  const visibleItems = getVisibleItems();

  // Boolean list with chips
  if (isBoolList) {
    return (
      <Box className={`${classes.container} ${isListHidden && !showHidden ? classes.containerHidden : ''}`}>
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <Typography variant="body2">
              {variable.name}
              {variable.required && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            {isListDerived && (
              <Chip size="small" label="derived" className={`${classes.badge} ${classes.derivedBadge}`} />
            )}
            {value === null && (
              <Chip size="small" label="null" className={`${classes.badge} ${classes.nullBadge}`} />
            )}
          </Box>
          <Box className={classes.headerRight}>
            {variable.nullable && !disabled && (
              value !== null ? (
                <Tooltip title="Set to null">
                  <IconButton size="small" onClick={handleSetNull}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Restore value">
                  <IconButton size="small" onClick={handleRestoreFromNull}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )
            )}
          </Box>
        </Box>
        {variable.description && (
          <Typography variant="caption" color="textSecondary">
            {variable.description}
          </Typography>
        )}
        {value !== null && (
          <>
            <Box className={classes.chipContainer}>
              {visibleItems.map(({ item, index }) => {
                const isItemHidden = item === null || isDerivedValue(item);
                const chipClass = item === null
                  ? classes.boolNull
                  : item === true
                    ? classes.boolTrue
                    : classes.boolFalse;

                return (
                  <Chip
                    key={index}
                    label={item === null ? 'null' : String(item)}
                    onDelete={disabled ? undefined : () => handleRemoveItem(index)}
                    size="small"
                    className={`${classes.boolChip} ${chipClass} ${isItemHidden && showHidden ? classes.chipHidden : ''}`}
                    onClick={
                      !disabled
                        ? () => {
                            // Cycle through: true -> false -> null (if nullable) -> true
                            if (item === true) handleItemChange(index, false);
                            else if (item === false) handleItemChange(index, variable.nullable ? null : true);
                            else handleItemChange(index, true);
                          }
                        : undefined
                    }
                  />
                );
              })}
            </Box>
            {!disabled && (
              <Box className={classes.inlineAddContainer}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setAddDialogOpen(true)}
                >
                  Add Boolean
                </Button>
              </Box>
            )}
            {hiddenItemCount > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={() => setShowHidden(!showHidden)}
                className={classes.showHiddenButton}
              >
                {showHidden ? 'Hide null values' : `Show ${hiddenItemCount} hidden`}
              </Button>
            )}
          </>
        )}
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
        <AddItemDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onAdd={newItem => onChange([...items, newItem])}
          elementType="bool"
          allowNull={variable.nullable}
        />
      </Box>
    );
  }

  // For simple string lists, use chip-style input with inline add
  if (isSimpleType && elementType === 'string') {
    return (
      <Box className={`${classes.container} ${isListHidden && !showHidden ? classes.containerHidden : ''}`}>
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <Typography variant="body2">
              {variable.name}
              {variable.required && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            {isListDerived && (
              <Chip size="small" label="derived" className={`${classes.badge} ${classes.derivedBadge}`} />
            )}
            {value === null && (
              <Chip size="small" label="null" className={`${classes.badge} ${classes.nullBadge}`} />
            )}
          </Box>
          <Box className={classes.headerRight}>
            {variable.nullable && !disabled && (
              value !== null ? (
                <Tooltip title="Set to null">
                  <IconButton size="small" onClick={handleSetNull}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Restore value">
                  <IconButton size="small" onClick={handleRestoreFromNull}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )
            )}
          </Box>
        </Box>
        {variable.description && (
          <Typography variant="caption" color="textSecondary">
            {variable.description}
          </Typography>
        )}
        <ValidationHints validation={variable.validation} type="string" />
        {value !== null && (
          <>
            <Box mt={1}>
              {visibleItems.map(({ item, index }) => {
                const isItemHidden = item === null || isDerivedValue(item);
                const stringValue = item === null ? '' : String(item);
                // Calculate width based on content (min 20ch, expand as needed)
                const inputWidth = Math.max(20, Math.min(stringValue.length + 2, 60));
                const shouldFocus = focusNewItemIndex === index;

                return (
                  <Box
                    key={index}
                    className={`${classes.stringItemRow} ${isItemHidden && showHidden ? classes.chipHidden : ''}`}
                  >
                    {item === null ? (
                      <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                        (null)
                      </Typography>
                    ) : (
                      <TextField
                        size="small"
                        variant="outlined"
                        value={stringValue}
                        onChange={e => handleItemChange(index, e.target.value)}
                        disabled={disabled}
                        inputRef={shouldFocus ? newItemRef : undefined}
                        inputProps={{
                          style: { width: `${inputWidth}ch` },
                        }}
                        placeholder={stringValue === '' ? 'Enter value' : undefined}
                      />
                    )}
                    {!disabled && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveItem(index)}
                        className={classes.deleteButton}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
            {!disabled && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleAddItemInline('')}
                className={classes.addButton}
              >
                Add
              </Button>
            )}
            {hiddenItemCount > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={() => setShowHidden(!showHidden)}
                className={classes.showHiddenButton}
              >
                {showHidden ? 'Hide null/derived values' : `Show ${hiddenItemCount} hidden`}
              </Button>
            )}
          </>
        )}
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // For object lists, use CollapsibleItem for each item
  if (isObjectList && effectiveElementSchema) {
    return (
      <Box className={`${classes.container} ${isListHidden && !showHidden ? classes.containerHidden : ''}`}>
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <Typography variant="body2">
              {variable.name}
              {variable.required && <span style={{ color: 'red' }}> *</span>}
              <Typography
                component="span"
                variant="caption"
                color="textSecondary"
                style={{ marginLeft: 8 }}
              >
                ({visibleItems.length}
                {hiddenItemCount > 0 && !showHidden ? ` of ${items.length}` : ''} item
                {visibleItems.length !== 1 ? 's' : ''})
              </Typography>
            </Typography>
            {isListDerived && (
              <Chip size="small" label="derived" className={`${classes.badge} ${classes.derivedBadge}`} />
            )}
            {value === null && (
              <Chip size="small" label="null" className={`${classes.badge} ${classes.nullBadge}`} />
            )}
          </Box>
          <Box className={classes.headerRight}>
            {variable.nullable && !disabled && (
              value !== null ? (
                <Tooltip title="Set to null">
                  <IconButton size="small" onClick={handleSetNull}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Restore value">
                  <IconButton size="small" onClick={handleRestoreFromNull}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )
            )}
          </Box>
        </Box>
        {variable.description && (
          <Typography variant="caption" color="textSecondary" paragraph>
            {variable.description}
          </Typography>
        )}

        {value !== null && (
          <>
            {visibleItems.map(({ item, index }) => {
              const isItemNull = item === null;
              const isItemDerived = isDerivedValue(item);

              return (
                <CollapsibleItem
                  key={index}
                  title={`Item ${index + 1}${isItemNull ? ' (null)' : ''}`}
                  subtitle={isItemNull ? 'null' : getObjectSummary(item as Record<string, unknown>)}
                  defaultExpanded={index === items.length - 1 && !isItemNull}
                  actions={
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {isItemDerived && (
                        <Chip size="small" label="derived" className={`${classes.badge} ${classes.derivedBadge}`} />
                      )}
                      {isItemNull && (
                        <Tooltip title="Restore value">
                          <IconButton
                            size="small"
                            onClick={e => { e.stopPropagation(); handleRestoreItem(index); }}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!isItemNull && variable.nullable && (
                        <Tooltip title="Set to null">
                          <IconButton
                            size="small"
                            onClick={e => { e.stopPropagation(); handleSetItemNull(index); }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <ItemActions
                        onDuplicate={() => handleDuplicateItem(index)}
                        onDelete={() => handleRemoveItem(index)}
                        disabled={disabled}
                      />
                    </Box>
                  }
                >
                  {!isItemNull && (
                    <ObjectItemEditor
                      schema={effectiveElementSchema!}
                      value={item as Record<string, unknown>}
                      onChange={newValue => handleItemChange(index, newValue)}
                      disabled={disabled}
                      depth={depth + 1}
                    />
                  )}
                </CollapsibleItem>
              );
            })}

            {!disabled && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                className={classes.addButton}
                variant="outlined"
              >
                Add Item
              </Button>
            )}

            {hiddenItemCount > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={() => setShowHidden(!showHidden)}
                className={classes.showHiddenButton}
              >
                {showHidden ? 'Hide null/derived items' : `Show ${hiddenItemCount} hidden`}
              </Button>
            )}
          </>
        )}

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // For number lists and other types
  return (
    <Box className={`${classes.container} ${isListHidden && !showHidden ? classes.containerHidden : ''}`}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Typography variant="body2">
            {variable.name}
            {variable.required && <span style={{ color: 'red' }}> *</span>}
            <Typography
              component="span"
              variant="caption"
              color="textSecondary"
              style={{ marginLeft: 8 }}
            >
              ({visibleItems.length}
              {hiddenItemCount > 0 && !showHidden ? ` of ${items.length}` : ''} item
              {visibleItems.length !== 1 ? 's' : ''})
            </Typography>
          </Typography>
          {isListDerived && (
            <Chip size="small" label="derived" className={`${classes.badge} ${classes.derivedBadge}`} />
          )}
          {value === null && (
            <Chip size="small" label="null" className={`${classes.badge} ${classes.nullBadge}`} />
          )}
        </Box>
        <Box className={classes.headerRight}>
          {variable.nullable && !disabled && (
            value !== null ? (
              <Tooltip title="Set to null">
                <IconButton size="small" onClick={handleSetNull}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Restore value">
                <IconButton size="small" onClick={handleRestoreFromNull}>
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )
          )}
        </Box>
      </Box>
      {variable.description && (
        <Typography variant="caption" color="textSecondary" paragraph>
          {variable.description}
        </Typography>
      )}

      {value !== null && (
        <>
          {visibleItems.map(({ item, index }) => {
            const isItemNull = item === null;
            const isItemHidden = isItemNull || isDerivedValue(item);
            const shouldFocus = focusNewItemIndex === index;
            const isEmpty = item === '' || item === 0;

            return (
              <Box
                key={index}
                className={`${classes.itemRow} ${isItemHidden && showHidden ? classes.itemRowHidden : ''}`}
              >
                <Box className={classes.itemContent}>
                  {isItemNull ? (
                    <Typography variant="body2" color="textSecondary">
                      (null)
                    </Typography>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      type={elementType === 'number' ? 'number' : 'text'}
                      value={item ?? ''}
                      onChange={e =>
                        handleItemChange(
                          index,
                          elementType === 'number'
                            ? Number(e.target.value)
                            : e.target.value,
                        )
                      }
                      disabled={disabled}
                      inputRef={shouldFocus ? newItemRef : undefined}
                      placeholder={isEmpty ? 'Enter value' : `Item ${index + 1}`}
                    />
                  )}
                </Box>
                <Box className={classes.itemActions}>
                  {isItemNull && (
                    <Tooltip title="Restore value">
                      <IconButton size="small" onClick={() => handleRestoreItem(index)}>
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!isItemNull && variable.nullable && (
                    <Tooltip title="Set to null">
                      <IconButton size="small" onClick={() => handleSetItemNull(index)}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <ItemActions
                    onDuplicate={() => handleDuplicateItem(index)}
                    onDelete={() => handleRemoveItem(index)}
                    disabled={disabled}
                  />
                </Box>
              </Box>
            );
          })}

          {!disabled && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                // For simple types, add inline; for complex types, use dialog
                if (elementType === 'string' || elementType === 'any') {
                  handleAddItemInline('');
                } else if (elementType === 'number') {
                  handleAddItemInline(0);
                } else {
                  setAddDialogOpen(true);
                }
              }}
              className={classes.addButton}
              variant="outlined"
            >
              Add Item
            </Button>
          )}

          {hiddenItemCount > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={() => setShowHidden(!showHidden)}
              className={classes.showHiddenButton}
            >
              {showHidden ? 'Hide null/derived items' : `Show ${hiddenItemCount} hidden`}
            </Button>
          )}
        </>
      )}

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}

      <AddItemDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={newItem => onChange([...items, newItem])}
        elementType={elementType}
        allowNull={variable.nullable}
      />
    </Box>
  );
};

// Sub-component for editing object items within a list
const ObjectItemEditor: React.FC<{
  schema: TerraformVariableSchema;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  disabled?: boolean;
  depth?: number;
}> = ({ schema, value, onChange, disabled, depth = 0 }) => {
  const objectValue = value || {};

  if (!schema.objectSchema) {
    return (
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        value={JSON.stringify(objectValue)}
        onChange={e => {
          try {
            onChange(JSON.parse(e.target.value));
          } catch {
            // Keep current value if not valid JSON
          }
        }}
        disabled={disabled}
      />
    );
  }

  return (
    <Box>
      {Object.entries(schema.objectSchema).map(([key, fieldSchema]) => (
        <Box key={key} mb={1}>
          <VariableEditor
            variable={{ ...fieldSchema, name: key }}
            value={objectValue[key]}
            onChange={newValue =>
              onChange({ ...objectValue, [key]: newValue })
            }
            disabled={disabled}
            depth={depth}
          />
        </Box>
      ))}
    </Box>
  );
};
